/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, Fragment } from 'preact';
import { useEffect, useRef } from 'preact/compat';
import { classNames } from '../utils/UNSAFE_classNames';
import { baseStyle } from './themes/FocusTrackerStyles.css';
import {
  FocusableElement,
  getActiveElement,
  allTabbableElements
} from '../utils/PRIVATE_tabbableUtils';
import { FocusGuard } from './FocusGuard';

type Props = {
  /**
   * The children that focus tracking should be handled on.
   */
  children: ComponentChildren;

  /**
   * Sets whether focus is tracked and whether guards are present.
   * @default false
   */
  isDisabled?: boolean;

  /**
   * Optional callback to be notified that the tracking is starting (when mounted and changing isDisabled from true to false).
   */
  onStartTracking?: (detail: {
    activeElement: FocusableElement;
    hasFocusWithin: boolean;
    tabbableElements: FocusableElement[];
  }) => void;

  /**
   * Optional callback to be notified that the tracking is stopping (when unmounted and changing isDisabled from false to true).
   */
  onStopTracking?: (detail: {
    hasFocusWithin: boolean;
    lastActiveElement: FocusableElement | null;
  }) => void;

  /**
   * Optional callback to to be notified when the start focus guard gains focus
   */
  onFocusStartEdge?: (detail: { tabbableElements: FocusableElement[] }) => void;

  /**
   * Optional callback to to be notified when the end focus guard gains focus
   */
  onFocusEndEdge?: (detail: { tabbableElements: FocusableElement[] }) => void;

  /**
   * Optional style class to be applied to the outer div of the tracker
   */
  styleClass?: string;
};

/**
 * Tracks focus within a DOM node.
 */
export const FocusTracker = ({
  children,
  onFocusStartEdge,
  onFocusEndEdge,
  onStartTracking,
  onStopTracking,
  isDisabled = false,
  styleClass
}: Props) => {
  const trackerRef = useRef<HTMLDivElement>(null);
  const startGuardRef = useRef<HTMLDivElement>(null);
  const endGuardRef = useRef<HTMLDivElement>(null);
  const lastActiveElementRef = useRef<FocusableElement | null>(null);
  const onStartTrackingRef = useRef<Props['onStartTracking']>();
  const onStopTrackingRef = useRef<Props['onStopTracking']>();

  // classes to be set on the children wrapper
  const classes = classNames([baseStyle, styleClass]);

  // store onStartTracking in our own ref to avoid this callback being triggered if it is ever updated externally.
  // for example, if we were not using a ref here and the component controlling this focus tracker passed in a
  // new onStartTracking callback function for whatever reason, the useEffect that invokes this callback would be
  // triggered at that time, and the new instance of this callback would be triggered as well.
  useEffect(() => {
    onStartTrackingRef.current = onStartTracking;
  }, [onStartTracking]);

  // store onStopTracking in our own ref to avoid this callback being triggered if it is ever updated externally.
  // for example, if we were not using a ref here and the component controlling this focus tracker passed in a
  // new onStopTracking callback function for whatever reason, the useEffect that invokes this callback would be
  // triggered at that time, and the previous instance of this callback would be triggered as well.
  useEffect(() => {
    onStopTrackingRef.current = onStopTracking;
  }, [onStopTracking]);

  // helper to handle guard focus
  const handleGuardFocus = (
    event: FocusEvent,
    trackerElement: HTMLDivElement,
    focusCallbackFunc?: Props['onFocusStartEdge'] // same as Props['onFocusEndEdge']
  ) => {
    if (focusCallbackFunc != null) {
      focusCallbackFunc({
        tabbableElements: allTabbableElements(trackerElement)
      });
    }
    // prevent scroll jump and bubbling this event up as a focus event
    event.stopPropagation();
    event.preventDefault();
  };

  // notify focus on start guard
  const handleOnFocusStartGuard = (event: FocusEvent) => {
    if (trackerRef.current != null) {
      handleGuardFocus(event, trackerRef.current, onFocusStartEdge);
    }
  };

  // notify focus on end guard
  const handleOnFocusEndGuard = (event: FocusEvent) => {
    if (trackerRef.current != null) {
      handleGuardFocus(event, trackerRef.current, onFocusEndEdge);
    }
  };

  // handle focus on child or other descendant
  const handleOnFocus = (event: FocusEvent) => {
    lastActiveElementRef.current = event.target as FocusableElement;
  };

  // handle initial render or re-render when 'isDisabled' changes
  useEffect(() => {
    // determines if this focus tracker contains real browser focus
    const _hasFocusWithin = () => {
      if (trackerRef.current != null) {
        return hasFocus([trackerRef.current, startGuardRef.current, endGuardRef.current]);
      }
      return false;
    };

    const trackerElement = trackerRef.current;
    if (trackerElement != null && !isDisabled) {
      if (onStartTrackingRef.current != null) {
        onStartTrackingRef.current({
          activeElement: getActiveElement(trackerElement),
          hasFocusWithin: _hasFocusWithin(),
          tabbableElements: allTabbableElements(trackerElement)
        });
      }

      // the 'cleanup' function of a useEffect is run when unmounted as well as before the useEffect is run on a re-render
      // 'isDisabled' is the only trigger so this should only run when unmounted and on isDisabled changes from false to true
      return () => {
        if (onStopTrackingRef.current != null) {
          onStopTrackingRef.current({
            hasFocusWithin: _hasFocusWithin(),
            lastActiveElement: lastActiveElementRef.current
          });
        }
      };
    }
    return;
    // it is very important that this useEffect is ONLY dependent on isDisabled changes. otherwise, the
    // start / stop tracking callbacks will be triggered whenever those other dependencies are updated.
  }, [isDisabled]);

  return (
    <Fragment>
      {!isDisabled && <FocusGuard onFocus={handleOnFocusStartGuard} ref={startGuardRef} />}
      <div onFocus={!isDisabled ? handleOnFocus : undefined} ref={trackerRef} class={classes}>
        {children}
      </div>
      {!isDisabled && <FocusGuard onFocus={handleOnFocusEndGuard} ref={endGuardRef} />}
    </Fragment>
  );
};

/**
 * Helper function used to help determine if real browser focus is currently contained in the
 * array of elements provided.
 */
const hasFocus = (elements: (FocusableElement | null)[]) => {
  if (elements.length === 0) {
    return false;
  }
  const activeElement = getActiveElement(elements[0] as Node);
  return elements.some((el) => el?.contains(activeElement));
};
